<template>
  <AntSpin :spinning="spinVisible" wrapperClassName="draft-change-spin">
    <div ref="highlightRootEl" class="draft-change-highlight-root">
    <AntRow class="draft-change-row">
      <AntCol
        :span="getColSpan('tree')"
        class="change-col change-tree-col"
      >
        <div class="change-tree-body">
          <AntTree
            class="published-docs-tree"
            :selected-keys="selectedKeys"
            :treeData="treeData"
            :fieldNames="{ key: 'docId' }"
            @select="onSelect"
          >
          <template #title="{ docId, docShortTitle }">
            <AntTag :color="docId.includes('master') ? 'orange' : 'blue'">
              {{ docId.includes('master') ? '母本' : '子本' }}
            </AntTag>
            {{ docShortTitle }}
            <AntSwitch
              v-if="!readOnly && !docId.includes('master') && type === 'publish'"
              v-model:checked="publishedDocsDataByDocId[docId].confirmed"
              size="small"
              class="confirmed-switch"
              checkedChildren="已確認"
              unCheckedChildren="未確認"
              @click="e => { if(e && e.stopPropagation) e.stopPropagation() }"
            />
          </template>
          </AntTree>
        </div>
      </AntCol>

      <AntCol
        :span="getColSpan('diff')"
        class="change-col change-diff-col"
      >
        <div
          v-if="!selectedDocId?.includes('master') && publishedDocsDataByDocId[selectedDocId]"
          class="preview-header"
        >
          <DocPlatformInfo
            :key="`${selectedDocId}-${platformInfoResetKey}`"
            collapsible
            :bordered="false"
            :docData="publishedDocsDataByDocId[selectedDocId].docData"
            :platformsMap="entirePlatformsMap"
          />
        </div>

        <div class="preview-body">
        <div
          v-if="selectedUpdatedDocFieldDiffs?.length"
          class="updated-doc-diff"
        >
          <h3 class="updated-doc-diff-title">
            子本設定變更
          </h3>
          <div
            v-for="row in selectedUpdatedDocFieldDiffs"
            :key="row.key"
            class="platform-info-row updated-doc-diff-row"
          >
            <div class="platform-info-label">
              {{ row.label }}:
            </div>
            <template v-if="row.isPlatforms">
              <DiffTagListCompare
                :fieldKey="row.key"
                :oldValue="row.beforeIds"
                :newValue="row.afterIds"
                :getLabel="id => entirePlatformsMap[id]"
                :keyword="highlightKeyword"
              />
            </template>
            <template v-else>
              <code class="ant-tag-red">{{ row.beforeText }}</code>
              =>
              <code class="ant-tag-green">{{ row.afterText }}</code>
            </template>
          </div>
        </div>

        <div v-if="isDev">
          <div>selectedKeys: {{ selectedKeys }}</div>
          <div>selectedDocId: {{ selectedDocId }}</div>
          <div>collapseActiveKeys: {{ collapseActiveKeys }}</div>
          <DevDebugDataBlock title="stagedSection" :data="stagedSection" maxHeight="200px" />
          <DevDebugDataBlock title="draftSection" :data="draftSection" maxHeight="200px" />
          <DevDebugDataBlock
            :title="`publishedDocsDataByDocId[${selectedDocId}]`"
            :data="publishedDocsDataByDocId[selectedDocId]"
            maxHeight="200px"
          />
        </div>

        <template v-if="Object.keys(publishedDocsDataByDocId[selectedDocId]?.diffDataBySectionId || {}).length">
          <h3>區塊變更</h3>

          <AntCollapse
            v-model:activeKey="collapseActiveKeys"
            style="margin-top: 16px;"
          >
            <AntCollapsePanel
              v-for="[sectionId, {
                editorUsername,
                updatedTime,
                originSectionData,
                sectionData,
                sectionDiffData = {},
                fieldsDiffData = [],
              }] in Object.entries(publishedDocsDataByDocId[selectedDocId].diffDataBySectionId || {})"
              :key="`${(originSectionData || sectionData || {}).docId || selectedDocId}-${sectionId}`"
            >
              <template #header>
                <div style="width: 100%; display: flex; justify-content: space-between; gap: 16px;">
                  <div>
                    {{ getSectionTitle(sectionData || sectionDiffData.after || originSectionData || {}) }}{{ (sectionData || sectionDiffData.after || originSectionData || {}).remark ? ` ${(sectionData || sectionDiffData.after || originSectionData).remark}` : '' }}
                    <span v-if="isDev" style="color:BurlyWood;">{{ ` ${(originSectionData || {}).docId}-${sectionId}` }}</span>
                  </div>
                  <div v-if="!readOnly" class="updated-info">
                    <div v-if="!stagedSection?.sectionId?.startsWith('staged-delete-')">Editor: {{ editorUsername }}</div>
                    <div>{{ updatedTime }}</div>
                  </div>
                </div>
              </template>

              <div style="display: flex; flex-direction: column; gap: 20px;">
                <div
                  v-if="isNoOpSectionDiff(sectionDiffData, fieldsDiffData)"
                  style="margin-left: 16px;"
                >
                  經系統比對您的更動與最初無異，若無其它更動，請執行「不」更新版號發佈
                </div>
                <PublishDiffBlock
                  v-for="(block, blockIdx) in buildMasterDiffBlocksFromSectionEntry({
                    originSectionData,
                    sectionData,
                    sectionDiffData,
                    fieldsDiffData,
                  })"
                  :key="blockIdx"
                  :block="block"
                  :getDocLabel="resolvePublishDocLabel"
                  :getPlatformLabel="id => entirePlatformsMap[id]"
                  :resolveSortOrderLabel="(row, side) => formatSortOrderChangeLabel(
                    side === 'old' ? sectionDiffData.before : sectionDiffData.after,
                    side === 'old' ? row.oldValue : row.newValue,
                    selectedDocId,
                    resolveSectionForTitleById,
                  )"
                  :resolveParentTitle="resolveParentSectionTitle"
                  :getTableColumn="key => originSectionData.tableColumns?.[key]"
                >
                  <template #after-headline>
                    <DevDebugDataBlock
                      v-if="isDev"
                      :title="`diffs: ${blockIdx}`"
                      :data="block.diffs"
                      maxHeight="200px"
                    />
                  </template>
                </PublishDiffBlock>
              </div>
            </AntCollapsePanel>
          </AntCollapse>
        </template>

        <div
          v-show="false"
          id="viewer"
        />
        </div>
      </AntCol>

      <AntCol
        v-if="showReleaseEditorCol"
        :span="getColSpan('editor')"
        class="change-col change-editor-col"
      >
        <div class="change-editor-body">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <h3>更版說明</h3>
          <div style="display: flex; align-items: center; gap: 8px;" v-if="!readOnly">
            <AntSwitch
              v-model:checked="publishedDocsDataByDocId[selectedDocId].shouldRelease"
              :disabled="readOnly"
            />
            <span>
              {{ publishedDocsDataByDocId[selectedDocId].shouldRelease ? '本次更動需更新版號' : '本次更動不更新版號' }}
            </span>
          </div>
        </div>

        <div
          v-show="readOnly || publishedDocsDataByDocId[selectedDocId].shouldRelease"
          style="margin-top: 16px;"
        >
          <div class="ant-row ant-form-item">
            <div class="ant-col ant-col-6 ant-form-item-label ant-form-item-label-left">
              <label>Released Version No</label>
            </div>
            <div class="ant-col ant-col-18 ant-form-item-control">
              <AntInput
                v-model:value="publishedDocsDataByDocId[selectedDocId].version"
                placeholder="Released Version No"
                :disabled="readOnly"
              />
            </div>
          </div>

          <div class="ant-row ant-form-item">
            <div class="ant-col ant-col-6 ant-form-item-label ant-form-item-label-left">
              <label>Update Date</label>
            </div>
            <div class="ant-col ant-col-18 ant-form-item-control">
              <AntInput
                v-model:value="publishedDocsDataByDocId[selectedDocId].updateDate"
                placeholder="Updated Date"
                :disabled="readOnly"
              />
            </div>
          </div>

          <div class="ant-form-item-label">
            <label>{{ getFieldLabel('content') }}</label>
          </div>
          <SectionEditor
            :html="true"
            :previewMode="readOnly"
            :readOnly="readOnly"
            :removedToolbarItems="['table', 'image']"
            :imageManagementData="imageManagementData"
            :sectionsDataByDocId="sectionsDataByDocId"
            :customizedVersionSelectedDocId="selectedDocId"
            :editorContent="publishedDocsDataByDocId[selectedDocId].releaseContent"
            :onChange="readOnly ? undefined : onChangeReleaseContent"
          />
        </div>
        </div>

      </AntCol>
    </AntRow>
    </div>
  </AntSpin>
</template>

<script>
import {
  ref,
  watch,
  inject,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue'

import moment from 'moment'

import { isEqual, isEmpty, cloneDeep } from 'lodash'

import {
  Row as AntRow,
  Col as AntCol,
  Spin as AntSpin,
  Tree as AntTree,
  Tag as AntTag,
  Input as AntInput,
  Switch as AntSwitch,
  Collapse as AntCollapse,
  message as antMessage,
} from 'ant-design-vue'

import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import PublishDiffBlock from '@/components/ApiDoc/publish/PublishDiffBlock.vue'
import DiffTagListCompare from '@/components/ApiDoc/shared/DiffTagListCompare.vue'
import DocPlatformInfo from '@/components/ApiDoc/doc/DocPlatformInfo.vue'
import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'

import { titleCase } from '@/utils'
import { isRootParentSectionId } from '../lib/constants'

import { applyDomSearchHighlight, clearDomSearchHighlight } from '../shared/searchHighlight'
import { useViewerEditor } from '../shared/viewerEditor'
import { getFieldLabel } from '../shared/fieldLabels'

import {
  shouldDisplay,
  sortDocIds,
  sortSectionsObj,
  getFieldPublishFlagsForDocPlatformChange,
  normalizeUpdatedDocByDocId,
  hasUpdatedDoc,
  getUpdatedDocIds,
  getUpdatedDocEntry,
  getSectionSortOrderForDoc,
  getSectionTitleDisplayNoForDoc,
} from '../lib/libsV3.js'

import {
  computeSectionPublishDocIds,
  buildUpdatedDocFieldDiffs,
  buildUpdatedDocFieldDiffsFromDocDiffs,
  buildMasterDiffBlocksFromSectionEntry,
  expandPublishedDocsFromActivityLog,
  formatSortOrderChangeLabel,
} from './publishDiff.js'

export default {
  name: 'DraftChange',
  components: {
    AntRow,
    AntCol,
    AntSpin,
    AntTree,
    AntTag,
    AntInput,
    AntSwitch,
    AntCollapse,
    AntCollapsePanel: AntCollapse.Panel,
    SectionEditor,
    PublishDiffBlock,
    DiffTagListCompare,
    DocPlatformInfo,
    DevDebugDataBlock,
  },
  props: {
    type: [String, Boolean],
    /** Activity Log 等場景：只讀顯示，不重算、不顯示確認／更版編輯 */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 已算好的發佈預覽快照（與 getPublishedDocsDataByDocId 結果同形）
     * 有值時直接顯示，不依 draftSection 重算
     */
    snapshotPublishedDocsDataByDocId: {
      type: Object,
      default: null,
    },
    /** Activity Log 搜尋關鍵字：畫面文字黃底（略過 Platforms 設定） */
    highlightKeyword: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    draftSection: Object,
    stagedSection: Object,
    originSectionsByWallet: Object,
    sectionsByWallet: Object,
    originFieldsByWallet: Object,
    releaseVerFieldsByDocId: Object,
    sectionsDataByDocId: Object,
    docsDataByDocId: Object,
    originalDocsDataByDocId: Object,
    platformOptions: Array,
    entirePlatformsMap: Object,
    imageManagementData: Array,
    onDiscardDraft: Function,
  },
  setup(props, { expose }) {
    const isDev = inject('isDev')
    useViewerEditor()

    const changeTypeTitleMap = {
      added: { en: 'Add', cn: '新增' },
      modified: { en: 'Update', cn: '更新' },
      removed: { en: 'Delete', cn: '删除' },
    }

    /** 僅「修改後又全部還原」才提示無異；新增／刪除看 type，diffs 為空 */
    const isNoOpSectionDiff = (sectionDiffData = {}, fieldsDiffData = []) => {
      if (fieldsDiffData.length) return false
      if ((sectionDiffData.type || 'modified') !== 'modified') return false
      const diffs = sectionDiffData.diffs || []
      if (!diffs.length) return false
      return diffs.every(d => isEqual(d.oldValue, d.newValue))
    }

    const spinVisible = ref(false)
    const platformInfoResetKey = ref(0)
    const highlightRootEl = ref(null)
    let highlightApplying = false
    let highlightRaf = 0
    let highlightObserver = null

    const publishedDocsDataByDocId = ref({})
    const selectedKeys = ref([])
    const selectedDocId = ref('')

    const resolveHighlightRoot = () => highlightRootEl.value || null

    const refreshSearchHighlight = () => {
      cancelAnimationFrame(highlightRaf)
      highlightRaf = requestAnimationFrame(() => {
        highlightRaf = requestAnimationFrame(() => {
          const root = resolveHighlightRoot()
          if (!root) return
          const keyword = (props.highlightKeyword || '').trim()
          if (!keyword) {
            clearDomSearchHighlight(root)
            return
          }
          highlightApplying = true
          try {
            applyDomSearchHighlight(root, keyword, {
              skipSelectors: ['.doc-platform-info'],
            })
          } finally {
            // 略過本次 mark 插入觸發的 MutationObserver
            queueMicrotask(() => { highlightApplying = false })
          }
        })
      })
    }

    const ensureHighlightObserver = () => {
      const root = resolveHighlightRoot()
      if (!root || highlightObserver) return
      highlightObserver = new MutationObserver(() => {
        if (highlightApplying) return
        if (!(props.highlightKeyword || '').trim()) return
        refreshSearchHighlight()
      })
      highlightObserver.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    const resolvePublishDocLabel = id => (
      props.docsDataByDocId?.[id]?.docShortTitle
      || publishedDocsDataByDocId.value?.[id]?.docShortTitle
      || id
    )

    const resolveParentSectionTitle = (sectionId) => {
      if (!sectionId || isRootParentSectionId(sectionId)) return ''
      return props.originSectionsByWallet?.[props.draftSection?.wallet]?.[sectionId]?.enTitle || ''
    }

    const getNextVersion = (version, carryNum = 99) => {
      if (!version) return '1.0.00'
      const [major, minor, patch] = version.split('.').map(Number)
      if (patch === carryNum && minor === carryNum) return `${major + 1}.0.0`
      if (patch === carryNum) return `${major}.${minor + 1}.0`
      return `${major}.${minor}.${String(patch + 1).padStart(2, '0')}`
    }

    const buildPlatformsMapForDoc = (docId) => {
      const docData = props.docsDataByDocId[docId] || {}
      const docPlatforms = docData.platforms || []
      const docExcludedPlatforms = docData.excludedPlatforms || []
      return props.platformOptions.reduce((acc, { value, label }) => {
        if (
          !docPlatforms.length
          || docExcludedPlatforms.includes(value)
          || docPlatforms.includes(value)
        ) acc[value] = label
        return acc
      }, { all: 'ALL' })
    }

    const getEffectiveDocPlatforms = (docData, allPlatformsList) => {
      if (!docData) return []
      const excluded = docData.platforms?.length ? [] : docData.excludedPlatforms || []
      return (docData.platforms || allPlatformsList).filter(p => !excluded.includes(p))
    }

    const resolveOriginSectionForWallet = (wallet, sectionId) => props.originSectionsByWallet[wallet]?.[sectionId] || props.sectionsByWallet[wallet]?.[sectionId]

    const pickExampleMetaCandidate = candidate => (
      candidate && typeof candidate === 'object' && !Array.isArray(candidate) ? candidate : null
    )

    const inferPlatformsFromExampleName = (exampleName = '') => {
      const match = String(exampleName).match(/\(([^)]+)\)\s*$/)
      if (!match) return []
      const token = match[1].trim()
      const platformId = Object.keys(props.entirePlatformsMap).find((id) => {
        if (id === token) return true
        const label = props.entirePlatformsMap[id]
        return label === token || id.startsWith(`${token}_`)
      })
      return platformId ? [platformId] : []
    }

    const resolveExampleMeta = (exampleId, {
      sectionData,
      beforeSectionData,
      afterSectionData,
      beforeObj,
      afterObj,
      wallet,
      sectionId,
    }) => {
      const fromWallet = wallet && sectionId
        ? props.sectionsByWallet[wallet]?.[sectionId]?.examples?.[exampleId]
        : undefined

      const fromOrigin = wallet && sectionId
        ? props.originSectionsByWallet[wallet]?.[sectionId]?.examples?.[exampleId]
        : undefined

      const candidates = [
        fromWallet,
        sectionData?.examples?.[exampleId],
        afterSectionData?.examples?.[exampleId],
        afterObj?.examples?.[exampleId],
        beforeSectionData?.examples?.[exampleId],
        beforeObj?.examples?.[exampleId],
        fromOrigin,
      ].map(pickExampleMetaCandidate).filter(Boolean)

      const withPlatforms = candidates.find(c => c.platforms?.length)
      const withExcludedPlatforms = candidates.find(c => c.excludedPlatforms?.length)
      const base = candidates[0] || {}
      const exampleName = base.exampleName || withPlatforms?.exampleName || withExcludedPlatforms?.exampleName
      const platforms = withPlatforms?.platforms?.length
        ? withPlatforms.platforms
        : inferPlatformsFromExampleName(exampleName)

      return {
        ...base,
        ...(platforms.length ? { platforms } : {}),
        ...(withExcludedPlatforms ? { excludedPlatforms: withExcludedPlatforms.excludedPlatforms } : {}),
        exampleId: base.exampleId || exampleId,
        exampleName,
      }
    }

    const filterFieldExamplesForDoc = (fieldObj, docData, exampleCtx) => {
      if (!fieldObj?.examples || docData.docId?.includes('master')) return fieldObj

      const nextExamples = Object.entries(fieldObj.examples).reduce((acc, [exampleId, value]) => {
        const meta = resolveExampleMeta(exampleId, exampleCtx)
        if (shouldDisplay(meta, docData)) acc[exampleId] = value
        return acc
      }, {})

      if (isEqual(nextExamples, fieldObj.examples)) return fieldObj
      if (!Object.keys(nextExamples).length) {
        const { examples, ...rest } = fieldObj
        return rest
      }
      return { ...fieldObj, examples: nextExamples }
    }

    const getPublishedDocsDataByDocId = async (draftSectionOverride = null) => {
      const effectiveDraftSection = draftSectionOverride || props.draftSection
      console.log('[StagedChange] getPublishedDocsDataByDocId', { effectiveDraftSection, entirePlatformsMap: props.entirePlatformsMap })
      const { wallet } = effectiveDraftSection

      const allPlatforms = Object.keys(props.entirePlatformsMap).slice(1) // 第一筆為「all」
      const allDocs = Object.keys(props.docsDataByDocId)
      const result = {}

      const getMergedDocData = (docId) => {
        const entry = getUpdatedDocEntry(effectiveDraftSection, docId)
        if (entry) return { ...props.docsDataByDocId[docId], ...entry, docId }
        return props.docsDataByDocId[docId]
      }

      const getPlatformStagedFieldFlags = (targetDocId, fieldData, fieldCustomizedVersion = {}) => {
        if (!getUpdatedDocEntry(effectiveDraftSection, targetDocId)) return null
        return getFieldPublishFlagsForDocPlatformChange({
          fieldData: {
            ...fieldData,
            ...(fieldCustomizedVersion?.[targetDocId] || {}),
          },
          docId: targetDocId,
          oldDocData: props.originalDocsDataByDocId[targetDocId],
          newDocData: getMergedDocData(targetDocId),
          allPlatforms,
          allDocs,
        })
      }

      const sortedSections = sortSectionsObj(
        Object.keys(effectiveDraftSection.updatedDataBySectionId || {})
          .map(sectionId => resolveOriginSectionForWallet(wallet, sectionId))
          .filter(Boolean),
      )
      console.log('[StagedChange] sortedSections', sortedSections)
      for (const { sectionId } of sortedSections) {
        const {
          sectionData: { customizedVersion: sectionCustomizedVersion = {}, ...sectionData } = {},
          fieldsData = [],
          editorUsername,
          updatedTime,
        } = effectiveDraftSection.updatedDataBySectionId[sectionId]
        // props.sectionsByWallet[sectionId].docs 有移除 doc 時，需用 props.originSectionsByWallet[sectionId].docs
        const originSection = props.originSectionsByWallet[wallet]?.[sectionId]
          ?? props.sectionsByWallet[wallet]?.[sectionId]
        if (!originSection) {
          console.warn('[StagedChange] skip section: origin not found', { wallet, sectionId })
          continue
        }
        const {
          customizedVersion: originSectionCustomizedVersion = {},
          ...originSectionData
        } = originSection

        // sectionDataIsEmpty = true => draft 中沒有 sectionData ，代表 section 沒有被變動
        const sectionDataIsEmpty = isEmpty(sectionData)

        const {
          sectionDocs,
          originSectionDocs,
          allDocIds,
          addedDocIds,
          removedDocIds,
        } = computeSectionPublishDocIds({
          wallet,
          sectionData,
          originSectionData,
          sectionDataIsEmpty,
        })

        const apisByDocId = { [`${wallet}#master`]: [] }
        const apiSectionIdsByDocId = { [`${wallet}#master`]: [] }
        if ((sectionDataIsEmpty ? originSectionData : sectionData).tag === 'request-time-interval-limits') {
          const apiSections = Object.values(props.sectionsByWallet[wallet])
            .filter((s) => {
              if (isRootParentSectionId(s.parentSectionId)) return false
              if (s.wallet === 'single') return s.tag === 'common-api-functions'
              if (s.wallet === 'multi') return s.tag === 'common-api-functions' || s.tag === 'multi-wallet-api-functions'
              return false
            })
            .sort((a, b) => a.sortOrder - b.sortOrder)

          apiSections.reduce((acc, section) => {
            acc[`${wallet}#master`].push(section.operationId)
            apiSectionIdsByDocId[`${wallet}#master`].push(section.sectionId);
            (section.docs || []).forEach((docId) => {
              if (!acc[docId]) acc[docId] = []
              acc[docId].push(section.operationId)
              if (!apiSectionIdsByDocId[docId]) apiSectionIdsByDocId[docId] = []
              apiSectionIdsByDocId[docId].push(section.sectionId)
            })
            return acc
          }, apisByDocId)
        }

        console.log('[StagedChange]', sectionData.enTitle || originSectionData.enTitle, `(${sectionId})`, {
          sectionData, sectionCustomizedVersion, originSectionData, originSectionCustomizedVersion, sectionDocs, originSectionDocs, allDocIds, addedDocIds, removedDocIds, apisByDocId, apiSectionIdsByDocId,
        })

        for (const docId of allDocIds) {
          // const isCustomizedDoc = !!sectionCustomizedVersion?.[docId]
          const isMaster = docId.includes('master')
          const docPlatforms = getEffectiveDocPlatforms(getMergedDocData(docId), allPlatforms)
          const oldDocPlatformsForDoc = getEffectiveDocPlatforms(props.originalDocsDataByDocId[docId], allPlatforms)

          const {
            customizedVersion: {
              [docId]: { examples, ...restCustomizedVersion } = {},
            } = {},
            ...sectionDataInSectionBySectionId
          } = props.sectionsByWallet[wallet][sectionId] || {}

          const { examples: _draftCvExamples, ...draftCustomizedForDoc } = sectionCustomizedVersion?.[docId] || {}

          const afterSectionData = {
            ...(sectionDataIsEmpty ? {
              ...sectionDataInSectionBySectionId,
              ...restCustomizedVersion,
            } : {
              ...sectionData,
              ...restCustomizedVersion,
              ...draftCustomizedForDoc,
            }),
            docId,
          }

          const beforeSectionData = {
            ...originSectionData,
            ...originSectionCustomizedVersion?.[docId] || {},
            docId,
          }

          if (!result[docId]) {
            const releaseVerFieldData = isMaster
              ? {}
              : Object.values(props.releaseVerFieldsByDocId[docId]?.row || {}).find(({ sortOrder }) => sortOrder === 0)
            // console.log('[StagedChange] releaseVersionSectionData', releaseVersionSectionData)
            const { version } = releaseVerFieldData || {}

            result[docId] = {
              docId,
              docTitle: isMaster ? `${titleCase(wallet)} Wallet` : props.docsDataByDocId[docId]?.docTitle,
              docShortTitle: isMaster ? `${titleCase(wallet)} Wallet` : props.docsDataByDocId[docId]?.docShortTitle,
              ...(isMaster ? {
                confirmed: true,
              } : {
                docData: props.docsDataByDocId[docId],
                version: getNextVersion(version),
                updateDate: moment().format('YYYY/MM/DD'),
                confirmed: false,
                shouldRelease: false,
              }),
            }
          }

          console.log('[StagedChange]', docId, sectionData.enTitle, `(${sectionId})`, 'sectionDataIsEmpty', sectionDataIsEmpty, { afterSectionData, beforeSectionData })
          if (!sectionDataIsEmpty) {
            let isSectionDiff = !isEqual(afterSectionData, beforeSectionData)

            // 父區塊 docs 新增其他子本時，不應讓既有子本也出現 section 變更
            if (
              isSectionDiff
              && !isMaster
              && !addedDocIds.includes(docId)
              && !removedDocIds.includes(docId)
            ) {
              const afterComparable = { ...afterSectionData }
              const beforeComparable = { ...beforeSectionData }
              delete afterComparable.docs
              delete beforeComparable.docs
              if (isEqual(afterComparable, beforeComparable)) {
                isSectionDiff = false
              }
            }

            console.log('[StagedChange]', docId, sectionData.enTitle, `(${sectionId})`, 'isSectionDiff', isSectionDiff, { afterSectionData, beforeSectionData })
            if (isSectionDiff) {
              const sectionPlatforms = sectionData.platforms || allPlatforms
              const originSectionPlatforms = originSectionData.platforms || allPlatforms

              const addedSectionPlatforms = sectionPlatforms.filter(p => !originSectionPlatforms.includes(p))
              const removedSectionPlatforms = originSectionPlatforms.filter(p => !sectionPlatforms.includes(p))

              const isAddedSection = addedSectionPlatforms.length > 0
                && docPlatforms.every(p => addedSectionPlatforms.includes(p))
              const isRemovedSection = removedSectionPlatforms.length > 0
                && docPlatforms.every(p => removedSectionPlatforms.includes(p))

              if (isAddedSection && !isMaster && !addedDocIds.includes(docId)) addedDocIds.push(docId)
              if (isRemovedSection && !isMaster && !removedDocIds.includes(docId)) removedDocIds.push(docId)

              console.log('[StagedChange]', docId, sectionData.enTitle, `(${sectionId})`, {
                sectionPlatforms, originSectionPlatforms, addedSectionPlatforms, removedSectionPlatforms, isAddedSection, isRemovedSection,
              })

              if (addedDocIds.includes(docId)) afterSectionData.added = true
              if (removedDocIds.includes(docId)) afterSectionData.deleted = true

              const diffs = generateDiff({ // eslint-disable-line no-use-before-define
                docId,
                beforeObj: beforeSectionData,
                afterObj: afterSectionData,
                sectionData: afterSectionData,
                beforeSectionData,
                afterSectionData,
                wallet,
                sectionId,
              })

              console.log('[StagedChange]', docId, sectionData.enTitle, `(${sectionId})`, 'diffs', diffs)

              if (diffs) {
                if (!result[docId].diffDataBySectionId) result[docId].diffDataBySectionId = {}
                result[docId].diffDataBySectionId[sectionId] = {
                  editorUsername,
                  updatedTime,
                  sectionDiffData: {
                    before: beforeSectionData,
                    after: afterSectionData,
                    ...diffs,
                  },
                  sectionData: afterSectionData,
                  originSectionData: beforeSectionData,
                }
              }
            }
          }

          if (fieldsData.length) {
            for (const fieldEntry of fieldsData) {
              const {
                customizedVersion: fieldCustomizedVersion = {},
                ...fieldData
              } = fieldEntry

              const { scope, fieldId } = fieldData

              const fieldPlatforms = fieldData.platforms || allPlatforms
              const fieldExcludedDocs = fieldData.platforms ? [] : fieldData.excludedDocs || []
              const fieldDocs = (fieldData.docs || allDocs).filter(doc => !fieldExcludedDocs.includes(doc))

              const {
                customizedVersion: originFieldCustomizedVersion = {},
                ...originFieldData
              } = props.originFieldsByWallet[wallet][sectionId]?.[scope]?.[fieldId] || {}

              const mergedFieldData = {
                ...fieldData,
                ...fieldCustomizedVersion?.[docId] || {},
                docId,
              }

              const mergedOriginFieldData = {
                ...originFieldData,
                ...originFieldCustomizedVersion?.[docId] || {},
                docId,
              }

              const isFieldDiff = !isEqual(mergedFieldData, mergedOriginFieldData)

              const otherUpdatedDocIds = getUpdatedDocIds(effectiveDraftSection).filter(id => id !== docId)

              const oldDocData = props.originalDocsDataByDocId[docId]
              const newDocData = getMergedDocData(docId)

              const platformChangeFlags = getUpdatedDocEntry(effectiveDraftSection, docId)
                ? getFieldPublishFlagsForDocPlatformChange({
                  fieldData: mergedFieldData,
                  docId,
                  oldDocData,
                  newDocData,
                  allPlatforms,
                  allDocs,
                })
                : null
              console.log('[StagedChange] field', docId, `${fieldData.fieldName || fieldData.platform} (${fieldData.sectionIdFieldId})`,
                {
                  docPlatforms,
                  oldDocPlatformsForDoc,
                  mergedFieldData,
                  mergedOriginFieldData,
                  addedDocIds,
                  removedDocIds,
                  platformChangeFlags,
                  isFieldDiff,
                  otherUpdatedDocIds,
                })
              if (!isFieldDiff && !platformChangeFlags) continue

              // 子本：field 在變更前後都不會顯示時，不應出現在 diff
              // - row：有 platform 時先對照 doc 有效 platforms；否則用 shouldDisplay（docs / excludedDocs / platforms…）
              // - request/response：一律 shouldDisplay（例：field.docs=['...#NTCY'] 時 IGK 不顯示）
              if (!isMaster) {
                const currentDocData = props.docsDataByDocId[docId] || {}
                let visibleBefore = shouldDisplay(mergedOriginFieldData, oldDocData)
                let visibleAfter = shouldDisplay(mergedFieldData, newDocData)

                if (scope === 'row') {
                  const rowPlatformBefore = mergedOriginFieldData.platform || originFieldData.platform
                  const rowPlatformAfter = mergedFieldData.platform || fieldData.platform || rowPlatformBefore
                  if (rowPlatformBefore) visibleBefore = oldDocPlatformsForDoc.includes(rowPlatformBefore)
                  if (rowPlatformAfter) visibleAfter = docPlatforms.includes(rowPlatformAfter)
                }

                if (!visibleBefore && !visibleAfter) continue

                // 以目前畫面上的 docData 再次確認，避免 originalDocsData 與當前草稿狀態基準不同造成誤顯示
                const visibleInCurrentDocBefore = shouldDisplay(mergedOriginFieldData, currentDocData)
                const visibleInCurrentDocAfter = shouldDisplay(mergedFieldData, currentDocData)
                if (!visibleInCurrentDocBefore && !visibleInCurrentDocAfter) continue
              }

              // 子本 platform 變更寫入草稿的 field，僅在對應子本顯示 added/deleted
              if (otherUpdatedDocIds.some(otherDocId => getPlatformStagedFieldFlags(otherDocId, fieldData, fieldCustomizedVersion))
                && !isFieldDiff) continue
              // 如果 docId 是 section.docs 新增或刪除的，則不顯示 field 差異
              if (addedDocIds.includes(docId) || removedDocIds.includes(docId)) continue

              // 變更後 doc 已不含此 row platform 時仍要顯示（子本移除 platform 的 [刪除] 列）
              if (scope === 'row'
                && !isMaster
                && (originFieldData.platform || fieldData.platform)
                && [originFieldData.platform, fieldData.platform].every(p => !docPlatforms.includes(p))
                && [originFieldData.platform, fieldData.platform].every(p => !oldDocPlatformsForDoc.includes(p))) continue

              if (scope === 'row'
                && !isMaster
                && (originFieldData.api || fieldData.api)
                && [originFieldData.api, fieldData.api].every(api => !apisByDocId[docId].includes(api))) continue

              if (!result[docId]) {
                const releaseVerFieldData = isMaster
                  ? {}
                  : Object.values(props.releaseVerFieldsByDocId[docId]?.row || {}).find(({ sortOrder }) => sortOrder === 0)
                // console.log('[StagedChange] releaseVersionSectionData', releaseVersionSectionData)
                const { version } = releaseVerFieldData || {}

                result[docId] = {
                  docId,
                  docTitle: isMaster ? `${titleCase(wallet)} Wallet` : props.docsDataByDocId[docId]?.docTitle,
                  docShortTitle: isMaster ? `${titleCase(wallet)} Wallet` : props.docsDataByDocId[docId]?.docShortTitle,
                  ...(isMaster ? {} : {
                    docData: props.docsDataByDocId[docId],
                    version: getNextVersion(version),
                    updateDate: moment().format('YYYY/MM/DD'),
                    confirmed: false,
                    shouldRelease: true,
                  }),
                }
              }

              const originFieldPlatforms = originFieldData.platforms || allPlatforms
              // if (scope === 'row' && originFieldData.platform) originFieldPlatforms = [originFieldData.platform]
              const originFieldExcludedDocs = originFieldData.platforms ? [] : originFieldData.excludedDocs || []
              const originFieldDocs = (originFieldData.docs || allDocs).filter(doc => !originFieldExcludedDocs.includes(doc))

              // 假設：abc參數Platform：SEXY => SEXY,PP ---- IN2(docPlatform=[PP,TADA])，發布版本時需顯示IN2新增abc參數
              // originFieldPlatforms = [SEXY]
              // fieldPlatforms = [SEXY,PP]
              // addedFieldPlatforms = [PP]
              // removedFieldPlatforms = []

              // 假設：abc參數Platform：BG,SEXY => BG,PG,PP ---- IN2(docPlatform=[PP,TADA])，發布版本時需顯示IN2新增abc參數
              // originFieldPlatforms = [BG,SEXY]
              // fieldPlatforms = [BG,PG,PP]
              // addedFieldPlatforms = [PG,PP]
              // removedFieldPlatforms = [SEXY]

              // 假設：abc參數Platform：ALL => SEXY
              // originFieldPlatforms = null ([ACEWIN,BG,...,SEXY,...])
              // fieldPlatforms = [SEXY]
              // addedFieldPlatforms = []
              // removedFieldPlatforms = [ACEWIN,BG,...] (no SEXY)
              // >>> IN2(docPlatform=[PP,TADA])，需顯示移除abc參數
              // >>> PRD(docPlatform=null)，不需顯示變動
              // >>> SANA(docPlatform=[FC,SEXY...])，不需顯示變動

              // 假設：abc參數Platform：SEXY => ALL
              // originFieldPlatforms = [SEXY]
              // fieldPlatforms = null ([ACEWIN,BG,...,SEXY,...])
              // addedFieldPlatforms = [ACEWIN,BG,...] (no SEXY)
              // removedFieldPlatforms = []
              // >>> IN2(docPlatform=[PP,TADA])，需顯示新增abc參數
              // >>> AAA(docPlatform=null+docExcludedPlatforms=[SEXY])，需顯示新增abc參數
              // >>> PRD(docPlatform=null)，不需顯示新增abc參數

              const addedFieldPlatforms = fieldPlatforms.filter(p => !originFieldPlatforms.includes(p))
              const removedFieldPlatforms = originFieldPlatforms.filter(p => !fieldPlatforms.includes(p))

              const addedFieldDocs = fieldDocs.filter(doc => !originFieldDocs.includes(doc))
              const removedFieldDocs = originFieldDocs.filter(doc => !fieldDocs.includes(doc))

              // originFieldData.platform = PP
              // fieldData.platform = FC
              // >>> PRD(docPlatform=null)，需顯示變動row platform變動：PP=>FC
              // >>> SANA(docPlatform=[FC,JDB.JILI,PG,PP,SEXY])，需顯示變動row platform變動：PP=>FC
              // >>> IN2(docPlatform=[PP,TADA])，需顯示移除row
              // >>> AAA(docPlatform=[FC,SEXY])，需顯示新增row

              // 對「其他子本 updatedDoc platform 變更」寫入的 field，不套用 platform 的 added/deleted 推斷
              const skipPlatformHeuristics = otherUpdatedDocIds.some(otherDocId => (
                getPlatformStagedFieldFlags(otherDocId, fieldData, fieldCustomizedVersion)
              ))

              let isAddedField = false
              let isRemovedField = false
              if (fieldData.added) {
                isAddedField = fieldDocs.includes(docId)
              } else if (platformChangeFlags) {
                isAddedField = !!platformChangeFlags.added
                isRemovedField = !!platformChangeFlags.deleted
              } else if (!skipPlatformHeuristics) {
                if (!isMaster) {
                  isAddedField = addedFieldDocs.includes(docId)
                  isRemovedField = removedFieldDocs.includes(docId)
                }

                if (!isMaster && addedFieldPlatforms.length && docPlatforms.every(p => addedFieldPlatforms.includes(p))) {
                  isAddedField = true
                }
                if (!isMaster && removedFieldPlatforms.length && docPlatforms.every(p => removedFieldPlatforms.includes(p))) {
                  isRemovedField = true
                }

                if (scope === 'row') {
                  if (fieldData.platform) {
                    isAddedField = isAddedField || (docPlatforms.includes(fieldData.platform) && !docPlatforms.includes(originFieldData.platform))
                  }
                  if (originFieldData.platform) {
                    isRemovedField = isRemovedField || (docPlatforms.includes(originFieldData.platform) && !docPlatforms.includes(fieldData.platform))
                  }
                }
              }

              console.log('[StagedChange]', docId, fieldData.platform || fieldData.api, `(${fieldData.sectionIdFieldId})`, {
                docPlatforms,
                fieldDocs,
                originFieldDocs,
                fieldPlatforms,
                originFieldPlatforms,
                fieldData,
                originFieldData,
                addedFieldPlatforms,
                removedFieldPlatforms,
                addedFieldDocs,
                removedFieldDocs,
                isAddedField,
                isRemovedField,
                platformChangeFlags,
              })

              if (!isAddedField && !isRemovedField && ((!isMaster && fieldData.added) || !isFieldDiff)) continue

              const after = {
                ...mergedFieldData,
                ...(isAddedField ? { added: true } : {}),
                ...(isRemovedField ? { deleted: true } : {}),
              }

              const before = {
                ...originFieldData,
                ...originFieldCustomizedVersion?.[docId] || {},
                docId,
              }

              const exampleCtx = {
                sectionData: sectionDataIsEmpty ? beforeSectionData : afterSectionData,
                beforeSectionData,
                afterSectionData,
                wallet,
                sectionId,
              }
              const oldDocDataForExamples = { ...oldDocData, docId }
              const newDocDataForExamples = { ...newDocData, docId }
              const beforeForDiff = !isMaster && scope !== 'row'
                ? filterFieldExamplesForDoc(before, oldDocDataForExamples, exampleCtx)
                : before
              const afterForDiff = !isMaster && scope !== 'row'
                ? filterFieldExamplesForDoc(after, newDocDataForExamples, exampleCtx)
                : after

              if (!isAddedField && !isRemovedField && !platformChangeFlags && isEqual(beforeForDiff, afterForDiff)) continue

              const diff = generateDiff({ // eslint-disable-line no-use-before-define
                docId,
                beforeObj: beforeForDiff,
                afterObj: afterForDiff,
                path: fieldData.scope === 'row' ? '/row' : '/field',
                sectionData: sectionDataIsEmpty ? beforeSectionData : afterSectionData, // 取得 tableColumns 用
                beforeSectionData,
                afterSectionData,
                wallet,
                sectionId,
                docDataForDisplay: newDocDataForExamples,
              })

              console.log('[StagedChange]', docId, 'diff', diff)

              if (diff) {
                if (!result[docId].diffDataBySectionId) result[docId].diffDataBySectionId = {}
                if (!result[docId].diffDataBySectionId[sectionId]) {
                  result[docId].diffDataBySectionId[sectionId] = {
                    editorUsername,
                    updatedTime,
                    fieldsDiffData: [],
                    sectionData: afterSectionData,
                    originSectionData: beforeSectionData,
                  }
                }
                if (!result[docId].diffDataBySectionId[sectionId].fieldsDiffData) result[docId].diffDataBySectionId[sectionId].fieldsDiffData = []

                result[docId].diffDataBySectionId[sectionId].fieldsDiffData.push({
                  before,
                  after,
                  ...diff,
                })
              }
            }

            try {
              const sectionDiffEntry = result[docId]?.diffDataBySectionId?.[sectionId]
              if (!sectionDiffEntry) continue

              const mergedSortOrderFieldsDiffData = mergeSortOrderDiff( // eslint-disable-line no-use-before-define
                sectionDiffEntry.fieldsDiffData,
                sectionDataIsEmpty ? beforeSectionData : afterSectionData,
              )
              sectionDiffEntry.fieldsDiffData = mergedSortOrderFieldsDiffData
              if (!mergedSortOrderFieldsDiffData.length && !sectionDiffEntry.sectionDiffData) {
                delete result[docId].diffDataBySectionId[sectionId]
              }
            } catch (error) {
              console.error('[StagedChange] mergeSortOrderDiff error', docId, sectionId, error)
            }
          }
        }
      }

      getUpdatedDocIds(effectiveDraftSection).forEach((docId) => {
        if (result[docId]) return
        const releaseVerFieldData = Object.values(props.releaseVerFieldsByDocId[docId]?.row || {}).find(({ sortOrder }) => sortOrder === 0)
        const { version } = releaseVerFieldData || {}

        result[docId] = {
          docId,
          docTitle: props.docsDataByDocId[docId]?.docTitle,
          docShortTitle: props.docsDataByDocId[docId]?.docShortTitle,
          docData: props.docsDataByDocId[docId],
          version: getNextVersion(version),
          updateDate: moment().format('YYYY/MM/DD'),
          confirmed: false,
          shouldRelease: false, // update doc 不用發布版本
          platformsMap: buildPlatformsMapForDoc(docId),
        }
      })

      Object.keys(result).forEach((docId) => {
        // master沒有變動，都是customized version或子本區塊順序變動
        const hasSectionDiffs = result[docId].diffDataBySectionId && !isEmpty(result[docId].diffDataBySectionId)
        const keepForUpdatedDoc = !!getUpdatedDocEntry(effectiveDraftSection, docId)
        if (!hasSectionDiffs && !keepForUpdatedDoc) {
          console.log('[StagedChange] delete docId because no diffDataBySectionId', docId, result[docId])
          delete result[docId]
        }
      })

      console.log('[StagedChange] publishedDocsDataByDocId', result)
      return result
    }

    const onSelect = (keys, { node }) => {
      console.log('[StagedChange] onSelect selectedKeys', selectedKeys, node.dataRef)
      if (!keys.length) return
      selectedKeys.value = keys
      selectedDocId.value = keys[0] // eslint-disable-line prefer-destructuring
    }

    // const platformsMap = computed(() => {
    //   console.log('[StagedChange] platformsMap', {
    //     docsDataByDocId: props.docsDataByDocId,
    //   })
    //   const { platforms: docPlatforms = [] } = props.docsDataByDocId[selectedDocId.value] || {}

    //   return props.platformOptions.reduce((acc, option) => {
    //     if (!acc.all) acc.all = 'ALL'
    //     if (!docPlatforms?.length || docPlatforms.includes(option.value)) acc[option.value] = option.label
    //     return acc
    //   }, {})
    // })

    const resolveSectionForTitleById = (wallet, sectionId, docId) => {
      if (!sectionId || isRootParentSectionId(sectionId)) return null

      const live = props.sectionsByWallet?.[wallet]?.[sectionId]
      const draftData = props.draftSection?.updatedDataBySectionId?.[sectionId]?.sectionData
      if (!live && !draftData) return null

      const merged = { ...(live || {}), ...(draftData || {}) }
      const customizedVersion = {
        ...(live?.customizedVersion || {}),
        ...(draftData?.customizedVersion || {}),
      }
      if (Object.keys(customizedVersion).length) {
        merged.customizedVersion = customizedVersion
      }

      const cvForDoc = customizedVersion[docId]
      if (cvForDoc && docId && !docId.includes('master')) {
        const { examples, ...cvFields } = cvForDoc
        Object.assign(merged, cvFields)
      }

      return merged
    }

    const getSectionTitle = (section = {}, options = {}) => {
      const {
        escape,
        lang,
      } = options
      const {
        docId, parentSectionId, wallet, sectionId,
      } = section
      const title = lang ? section[`${lang}Title`] : `${section.enTitle} ${section.cnTitle}`

      if (!docId) return title || ''

      const resolvedSection = resolveSectionForTitleById(wallet, sectionId, docId) || section

      if (isRootParentSectionId(parentSectionId)) {
        const displayNo = docId.includes('master')
          ? (resolvedSection.sortOrder ?? 0) + 1
          : getSectionTitleDisplayNoForDoc(resolvedSection, docId)
        return `${displayNo}${escape ? '\\' : ''}. ${title}`
      }

      const parentSection = resolveSectionForTitleById(wallet, parentSectionId, docId)
      if (!parentSection) {
        return `${getSectionSortOrderForDoc(resolvedSection, docId) + 1}. ${title}`
      }

      const parentNo = isRootParentSectionId(parentSection.parentSectionId)
        ? getSectionTitleDisplayNoForDoc(parentSection, docId)
        : getSectionSortOrderForDoc(parentSection, docId) + 1
      const childNo = getSectionSortOrderForDoc(resolvedSection, docId) + 1

      return `${parentNo}.${childNo}. ${title}`
    }

    const compactTitleParts = parts => (parts || []).filter(part => part && part.text !== '')

    const buildChangeTitle = ({
      type = 'modified',
      dataType = 'section',
      titleSection = {},
      afterObj = {},
      beforeObj = {},
      mainColumnValue = '',
    }) => {
      const { en: enOp, cn: cnOp } = changeTypeTitleMap[type] || changeTypeTitleMap.modified
      const scope = afterObj.scope || beforeObj.scope || ''
      const fieldName = afterObj.fieldName || beforeObj.fieldName || ''
      if (dataType === 'field') {
        return {
          en: `[${enOp}] ${scope} ${fieldName} field`,
          cn: `[${cnOp}] ${scope} ${fieldName} 参数`,
          default: `[${cnOp}] ${scope} ${fieldName} 参数`,
          parts: compactTitleParts([
            { type: 'text', text: `[${cnOp}] ${scope} ` },
            { type: 'code', text: fieldName },
            { type: 'text', text: ' 参数' },
          ]),
        }
      }
      if (dataType === 'row') {
        const cn = `[${cnOp}] ${mainColumnValue}`
        return {
          en: `[${enOp}] ${mainColumnValue}`,
          cn,
          default: cn,
          parts: compactTitleParts([{ type: 'text', text: cn }]),
        }
      }
      const cn = `[${cnOp}] ${getSectionTitle(titleSection)}`
      return {
        en: `[${enOp}] ${getSectionTitle(titleSection, { lang: 'en' })}`,
        cn: `[${cnOp}] ${getSectionTitle(titleSection, { lang: 'cn' })}`,
        default: cn,
        parts: compactTitleParts([{ type: 'text', text: cn }]),
      }
    }

    const getSectionTitleWithHtmlLink = (section = {}, options = {}) => {
      const { lang } = options
      return `<a href="${section.sectionId}_${lang}">${getSectionTitle(section, options)}</a>`
    }

    const isBooleanMap = val => (
      val && typeof val === 'object' && !Array.isArray(val)
      && Object.values(val).every(v => typeof v === 'boolean')
    )

    const getMainColumnValue = (sectionData = {}, afterObj = {}, beforeObj = {}) => {
      if ((afterObj.scope || beforeObj.scope) !== 'row') return ''
      const platformId = afterObj.platform || beforeObj.platform
      const tableColumns = sectionData?.tableColumns
      if (!tableColumns || typeof tableColumns !== 'object') {
        return props.entirePlatformsMap[platformId] || platformId || ''
      }
      const mainColumnId = Object.values(tableColumns).find(({ sortOrder }) => sortOrder === 0)?.columnId
      if (!mainColumnId) {
        return props.entirePlatformsMap[platformId] || platformId || ''
      }
      if (mainColumnId === 'platform') {
        return props.entirePlatformsMap[platformId] || platformId
      }
      console.log('getMainColumnValue', { tableColumns, mainColumnId })
      return afterObj[mainColumnId] || beforeObj[mainColumnId]
    }

    const getFieldExampleIdFromPath = (currentPath, key) => {
      if (currentPath === '/field/examples') return key
      const match = currentPath.match(/^\/field\/examples\/([^/]+)/)
      return match?.[1]
    }

    const generateDiff = ({
      docId,
      beforeObj,
      afterObj,
      path = '',
      sectionData,
      beforeSectionData,
      afterSectionData,
      wallet,
      sectionId,
      docDataForDisplay,
    }) => {
      const isMaster = docId.includes('master')
      const docData = docDataForDisplay || { ...props.docsDataByDocId[docId], docId }
      const exampleMetaCtx = {
        sectionData: sectionData || afterSectionData || afterObj,
        beforeSectionData,
        afterSectionData,
        beforeObj,
        afterObj,
        wallet,
        sectionId,
      }

      const shouldShowFieldExampleDiff = (exampleId) => {
        if (!exampleId || isMaster) return true
        return shouldDisplay(resolveExampleMeta(exampleId, exampleMetaCtx), docData)
      }

      const filterFieldExampleDiffs = (diffs) => {
        if (isMaster) return diffs
        return diffs.filter((diff) => {
          const exampleId = diff.path.match(/^\/field\/examples\/([^/]+)/)?.[1]
          if (!exampleId) return true
          return shouldShowFieldExampleDiff(exampleId)
        })
      }

      const getDiffs = (before, after, currentPath = '') => {
        const diffs = []
        // 1. 使用 Set 取出所有 Key 的聯集
        const allKeys = new Set([
          ...Object.keys(after ?? {}),
          ...Object.keys(before ?? {}),
        ])

        allKeys.forEach((key) => {
          let newVal = after?.[key]
          let oldVal = before?.[key]
          // const currentPath =

          // 如果內容完全相同，直接跳過
          if (isEqual(newVal, oldVal)) return

          // GameTable ID 的 tablePlatformsOrder 需要過濾掉不顯示的 platform
          if (key === 'tablePlatformsOrder') {
            newVal = newVal.filter(pId => shouldDisplay({ platforms: [pId] }, props.docsDataByDocId[docId]))
            oldVal = oldVal.filter(pId => shouldDisplay({ platforms: [pId] }, props.docsDataByDocId[docId]))
          }

          const fieldExampleId = getFieldExampleIdFromPath(currentPath, key)
          if (fieldExampleId && !shouldShowFieldExampleDiff(fieldExampleId)) return

          if (!isMaster && !currentPath.includes('/examples') && ['platforms', 'docs', 'excludedDocs'].includes(key)) return

          if (currentPath.startsWith('/examples/PoHeiwm0lP')) {
            console.log(docId, currentPath, {
              key, newVal, oldVal, beforeObj, afterObj,
            })
          }

          // section /examples/{exampleId} 需要 example.platforms 和 docData.platforms 判斷是否顯示
          if (!isMaster && currentPath === '/examples') {
            const exampleId = key
            const oldDisplayed = shouldDisplay(
              resolveExampleMeta(exampleId, { ...exampleMetaCtx, sectionData: beforeSectionData || beforeObj }),
              docData,
            )
            const newDisplayed = shouldDisplay(
              resolveExampleMeta(exampleId, { ...exampleMetaCtx, sectionData: afterSectionData || afterObj }),
              docData,
            )

            let type
            if (oldDisplayed && !newDisplayed) type = 'removed'
            if (!oldDisplayed && newDisplayed) type = 'added'
            if (oldDisplayed && newDisplayed) type = 'modified'
            console.log(docId, currentPath, {
              key, newVal, oldVal, type, oldDisplayed, newDisplayed, beforeExample: beforeObj?.examples?.[exampleId], afterExample: afterObj?.examples?.[exampleId],
            })
            if (type !== 'modified') {
              diffs.push({
                path: `${currentPath}/${key}`,
                key,
                type,
                oldValue: oldDisplayed ? newVal : null, // 用newVal而非oldVal是因為要讓顯示是after platforms的值
                newValue: newDisplayed ? newVal : null,
              })
              return
            }
          }

          // 判斷是否為「純物件」以決定是否遞迴 (排除 null 與 Array)
          const isBothObject = (val1, val2) => [val1, val2].every(v => typeof v === 'object' && v !== null && !Array.isArray(v))

          if (isBothObject(newVal, oldVal) && isBooleanMap(oldVal) && isBooleanMap(newVal)) {
            diffs.push({
              path: `${currentPath}/${key}`,
              key,
              oldValue: oldVal,
              newValue: newVal,
            })
          } else if (isBothObject(newVal, oldVal)) {
          // 遞迴進入下一層
            diffs.push(...getDiffs(oldVal, newVal, `${currentPath}/${key}`))
          } else {
            const diffItem = {
              path: `${currentPath}/${key}`,
              key,
            }

            // 2. 判斷狀態：新增、刪除、或修改
            const hasInNew = after !== null && Object.hasOwn(after ?? {}, key)
            const hasInOld = before !== null && Object.hasOwn(before ?? {}, key)

            if (hasInOld && !hasInNew) {
            // 刪除：只有舊的有
              diffItem.oldValue = oldVal
            } else if (!hasInOld && hasInNew) {
            // 新增：只有新的有
              diffItem.newValue = newVal
            } else {
            // 修改：兩者都有但值不同
              diffItem.oldValue = oldVal
              diffItem.newValue = newVal
            }
            diffs.push(diffItem)
          }
        })
        return diffs
      }

      let dataType = 'section'
      if (afterObj.fieldId) dataType = 'field'
      if (afterObj.scope === 'row') dataType = 'row'

      let type = 'modified'
      if (afterObj.added) type = 'added'
      if (afterObj.deleted) type = 'removed'

      const mainColumnValue = getMainColumnValue(sectionData, afterObj, beforeObj)

      const titleSection = (afterObj?.sectionId || afterObj?.enTitle || afterObj?.cnTitle) ? afterObj : beforeObj

      const diffs = type === 'modified'
        ? filterFieldExampleDiffs(getDiffs(beforeObj, afterObj, path))
        : []
      if (type === 'modified' && !diffs.length) return null

      return {
        path,
        type,
        diffs,
        title: buildChangeTitle({
          type,
          dataType,
          titleSection,
          afterObj,
          beforeObj,
          mainColumnValue,
        }),
      }
    }

    const mergeSortOrderDiff = (fieldsDiffData, sectionData) => {
      if (!fieldsDiffData?.length) return []

      const sortOrderDiffs = []
      const mergedSortOrderDiffData = fieldsDiffData.reduce((acc, data) => {
        const sortOrderDiff = (data.diffs || []).find(diff => diff.key === 'sortOrder')
        const otherDiffs = (data.diffs || []).filter(diff => diff.key !== 'sortOrder')
        if (sortOrderDiff) sortOrderDiffs.push({ ...data, sortOrderDiff })
        if (otherDiffs.length) acc.push({ ...data, diffs: otherDiffs })
        return acc
      }, [])
      console.log(sectionData.docId, 'mergeSortOrderDiff', { fieldsDiffData, mergedSortOrderDiffData, sortOrderDiffs })
      if (!sortOrderDiffs.length) return fieldsDiffData

      const oldValue = sortOrderDiffs.sort((a, b) => a.sortOrderDiff.oldValue - b.sortOrderDiff.oldValue)
        .map(({ before }) => getMainColumnValue(sectionData, before, before))
      const newValue = sortOrderDiffs.sort((a, b) => a.sortOrderDiff.newValue - b.sortOrderDiff.newValue)
        .map(({ after }) => getMainColumnValue(sectionData, after, after))

      console.log(sectionData.docId, 'mergeSortOrderDiff', { oldValue, newValue })
      if (isEqual(oldValue, newValue)) return mergedSortOrderDiffData

      mergedSortOrderDiffData.push({
        before: {},
        after: {},
        type: 'modified',
        title: {
          en: '[Update] Adjust Sort Order',
          cn: '[更新] 调整顺序',
          default: '[更新] 调整顺序',
          parts: [{ type: 'text', text: '[更新] 调整顺序' }],
        },
        diffs: [{
          path: '/field/sortOrder',
          key: 'sortOrder',
          oldValue,
          newValue,
        }],
      })

      return mergedSortOrderDiffData
    }

    const fillDocDiffsAndReleaseContent = async (docsDataByDocId, updatedDocByDocIdOverride = null) => {
      if (isEmpty(docsDataByDocId)) return
      const updatedDocByDocId = updatedDocByDocIdOverride ?? normalizeUpdatedDocByDocId(props.draftSection.updatedDoc)

      for (const docId of Object.keys(docsDataByDocId)) {
        if (docId.includes('master')) continue

        const updatedDocEntry = updatedDocByDocId[docId]
        if (updatedDocEntry) {
          const beforeDoc = props.originalDocsDataByDocId[docId]
          const platformsMap = docsDataByDocId[docId].platformsMap || buildPlatformsMapForDoc(docId)
          const diffs = generateDiff({
            docId,
            beforeObj: beforeDoc,
            // 需與 before 合併，否則未出現在 updatedDoc 的欄位會被誤判成刪除
            afterObj: { ...(beforeDoc || {}), ...updatedDocEntry, docId },
            platformsMap,
          })

          if (diffs) {
            docsDataByDocId[docId].docDiffs = Array.isArray(diffs) ? diffs : [diffs]
          }
          const fieldDiffRows = buildUpdatedDocFieldDiffs(
            beforeDoc || {},
            { ...(props.docsDataByDocId[docId] || {}), ...updatedDocEntry, docId },
            props.entirePlatformsMap || {},
          )
          if (fieldDiffRows.length) {
            docsDataByDocId[docId].updatedDocFieldDiffs = fieldDiffRows
          }
        }

        let enDiffContent = '<strong>Schedule of update - Staging environment Already released；Production environment Already released</strong><br>'
        let cnDiffContent = '<strong>上线时程 - 测试环境 已上线；正式环境 已上线</strong><br>'

        const diffDataBySectionIdEntries = Object.entries(docsDataByDocId[docId].diffDataBySectionId || {})
        diffDataBySectionIdEntries.forEach(([sectionId, { sectionDiffData, fieldsDiffData = [], originSectionData }], idx) => {
          const diffData = []
          if (sectionDiffData) diffData.push(sectionDiffData)
          if (fieldsDiffData.length) diffData.push(...fieldsDiffData)

          if (!diffData?.length || sectionId === 'ReleaseVer') return

          const isLastSection = idx === diffDataBySectionIdEntries.length - 1

          let enDiff = ''
          let cnDiff = ''
          diffData.forEach(({ title }, diffIdx) => {
            const isLastDiff = diffIdx === diffData.length - 1
            enDiff += `${title.en}<br>`
            cnDiff += `${title.cn}${isLastDiff && isLastSection ? '' : '<br>'}`
          })

          enDiffContent += `・${getSectionTitleWithHtmlLink(originSectionData, { lang: 'en' })}<br>${enDiff}<br>`
          cnDiffContent += `・${getSectionTitleWithHtmlLink(originSectionData, { lang: 'cn' })}<br>${cnDiff}${isLastSection ? '' : '<br>'}`
        })

        docsDataByDocId[docId].releaseContent = `${enDiffContent}${cnDiffContent}`

        if (!diffDataBySectionIdEntries.length && !docsDataByDocId[docId].docDiffs?.length) {
          // 僅 updatedDoc（子本 platforms 變更）時仍須保留於發佈列表
          if (updatedDocEntry) {
            const beforeDoc = props.originalDocsDataByDocId[docId] || {}
            const platformsMap = docsDataByDocId[docId].platformsMap || buildPlatformsMapForDoc(docId)
            const oldPlatforms = beforeDoc.platforms || []
            const newPlatforms = updatedDocEntry.platforms || []
            const added = newPlatforms.filter(p => !oldPlatforms.includes(p))
            const removed = oldPlatforms.filter(p => !newPlatforms.includes(p))
            if (added.length || removed.length) {
              const fmt = ids => (ids.length ? ids.map(p => platformsMap[p] || p).join(', ') : 'ALL')
              enDiffContent += `・Update sub-doc platforms 子本平台：${fmt(oldPlatforms)} => ${fmt(newPlatforms)}<br>`
              cnDiffContent += `・更新子本平台：${fmt(oldPlatforms)} => ${fmt(newPlatforms)}<br>`
              docsDataByDocId[docId].releaseContent = `${enDiffContent}${cnDiffContent}`
            }
          } else {
            delete docsDataByDocId[docId]
            console.log('DELETE DOC', docId, docsDataByDocId)
          }
        }
      }

      console.log('publishedDocsDataByDocId', docsDataByDocId)
    }

    const hasDraftPublishContent = (draftSection) => {
      if (!draftSection) return false
      return !isEmpty(draftSection.updatedDataBySectionId) || hasUpdatedDoc(draftSection)
    }

    const areDraftOriginsReady = (draftSection) => {
      const { wallet: draftWallet, updatedDataBySectionId = {} } = draftSection || {}
      const sectionIds = Object.keys(updatedDataBySectionId)
      if (!sectionIds.length) return true
      return sectionIds.every(sectionId => resolveOriginSectionForWallet(draftWallet, sectionId))
    }

    /** 刪除 platform 等 staged 發佈時，updatedDoc 在 stagedSection.draftSectionByWallet 而非 draftSection */
    const effectiveUpdatedDocByDocId = computed(() => {
      const isDeleteStaged = props.stagedSection?.sectionId?.startsWith('staged-delete-')
      if (isDeleteStaged && props.stagedSection?.draftSectionByWallet) {
        return Object.values(props.stagedSection.draftSectionByWallet).reduce(
          (acc, { updatedDoc }) => ({ ...acc, ...normalizeUpdatedDocByDocId(updatedDoc) }),
          {},
        )
      }
      return normalizeUpdatedDocByDocId(props.draftSection?.updatedDoc)
    })

    const hasSnapshot = () => !isEmpty(props.snapshotPublishedDocsDataByDocId)

    const selectedUpdatedDocFieldDiffs = computed(() => {
      const docId = selectedDocId.value
      if (!docId || docId.includes('master')) return []

      // Activity Log 快照：用發佈時存下的 updatedDocFieldDiffs／docDiffs
      if (hasSnapshot() || props.readOnly) {
        const entry = publishedDocsDataByDocId.value[docId]
        if (!entry) return []
        if (entry.updatedDocFieldDiffs?.length) return entry.updatedDocFieldDiffs
        return buildUpdatedDocFieldDiffsFromDocDiffs(
          entry.docDiffs,
          entry.docData || {},
          props.entirePlatformsMap || {},
        )
      }

      const updatedDocEntry = effectiveUpdatedDocByDocId.value[docId]
      if (!updatedDocEntry) return []

      const beforeDoc = props.originalDocsDataByDocId[docId] || {}
      const afterDoc = { ...props.docsDataByDocId[docId], ...updatedDocEntry, docId }
      return buildUpdatedDocFieldDiffs(beforeDoc, afterDoc, props.entirePlatformsMap || {})
    })

    /** 與 generateDiff 產出的 title 格式完全一致（供 Activity Log 快照重建用） */
    const buildPublishDiffTitle = ({
      type = 'modified',
      beforeObj = {},
      afterObj = {},
      sectionData,
    }) => {
      let dataType = 'section'
      if (afterObj.fieldId || beforeObj.fieldId) dataType = 'field'
      if (afterObj.scope === 'row' || beforeObj.scope === 'row') dataType = 'row'

      const mainColumnValue = getMainColumnValue(sectionData, afterObj, beforeObj)
      const titleSection = (afterObj?.sectionId || afterObj?.enTitle || afterObj?.cnTitle)
        ? afterObj
        : beforeObj
      return buildChangeTitle({
        type,
        dataType,
        titleSection,
        afterObj,
        beforeObj,
        mainColumnValue,
      })
    }

    const applySnapshotPublishedDocs = (snapshot) => {
      if (!snapshot || isEmpty(snapshot)) return
      const next = cloneDeep(expandPublishedDocsFromActivityLog(snapshot))
      // Activity Log：優先用快照內／傳入的 docs（發佈當下），勿用「現在」的 live 覆蓋已有 docData
      const snapshotDocs = props.docsDataByDocId || {}

      Object.keys(next).forEach((docId) => {
        const snapDoc = snapshotDocs[docId]
        if (snapDoc) {
          // Platforms 設定讀 entry.docData；有快照則以快照為準
          next[docId].docData = next[docId].docData || snapDoc
          const shortTitle = snapDoc.docShortTitle
            || next[docId].docShortTitle
            || next[docId].docData?.docShortTitle
          const title = snapDoc.docTitle
            || next[docId].docTitle
            || next[docId].docData?.docTitle
          if (shortTitle) {
            next[docId].docShortTitle = shortTitle
            if (next[docId].docData) next[docId].docData.docShortTitle = shortTitle
          }
          if (title) {
            next[docId].docTitle = title
            if (next[docId].docData) next[docId].docData.docTitle = title
          }
        }
        // 樹節點至少要有可顯示名稱
        if (!next[docId].docShortTitle) {
          next[docId].docShortTitle = next[docId].docData?.route
            || docId.split('#').pop()
            || docId
        }

        const diffBySection = next[docId].diffDataBySectionId || {}
        Object.values(diffBySection).forEach((sectionEntry) => {
          const sectionCtx = sectionEntry.sectionData
            || sectionEntry.sectionDiffData?.after
            || sectionEntry.originSectionData

          if (sectionEntry.sectionDiffData) {
            const sd = sectionEntry.sectionDiffData
            const beforeObj = sd.before || sectionEntry.originSectionData || {}
            const afterObj = sd.after || sectionEntry.sectionData || {}
            let type = sd.type || 'modified'
            if (afterObj.added) type = 'added'
            if (afterObj.deleted || sectionEntry.sectionData?.deleted) type = 'removed'
            sd.type = type
            sd.title = buildPublishDiffTitle({
              type,
              beforeObj,
              afterObj,
              sectionData: sectionCtx,
            })
            if (type !== 'modified') sd.diffs = []
          }

          const fieldsDiffData = sectionEntry.fieldsDiffData || []
          fieldsDiffData.forEach((fieldDiff) => {
            const beforeObj = fieldDiff.before || {}
            const afterObj = fieldDiff.after || {}
            let type = fieldDiff.type || 'modified'
            if (afterObj.added) type = 'added'
            if (afterObj.deleted) type = 'removed'
            fieldDiff.type = type
            fieldDiff.title = buildPublishDiffTitle({
              type,
              beforeObj,
              afterObj,
              sectionData: sectionCtx,
            })
            if (type !== 'modified') fieldDiff.diffs = []
          })
        })
      })

      publishedDocsDataByDocId.value = next
      selectedKeys.value = sortDocIds(Object.keys(next))
      selectedDocId.value = selectedKeys.value[0] || '' // eslint-disable-line prefer-destructuring
    }

    watch(
      [
        () => props.snapshotPublishedDocsDataByDocId,
        () => props.sectionsByWallet,
        () => props.docsDataByDocId,
      ],
      ([snapshot]) => {
        if (isEmpty(snapshot)) return
        applySnapshotPublishedDocs(snapshot)
        spinVisible.value = false
      },
      { immediate: true, deep: true },
    )

    // 遞增版號：每次 watch 重新執行都產生新的 runId，用來忽略舊 async 結果（避免覆蓋最新狀態）
    let publishedDocDiffRunId = 0
    watch([() => props.draftSection, () => props.stagedSection], ([newDraft, newStaged], _prev, onCleanup) => {
      // Activity Log 等快照模式：不依草稿重算
      if (hasSnapshot() || props.readOnly) return

      publishedDocDiffRunId += 1
      const runId = publishedDocDiffRunId

      let cancelled = false
      // cleanup 會在下一次 effect 執行前/元件卸載時觸發，將本次執行標記失效，防止過期請求落回寫入
      onCleanup(() => { cancelled = true });
      (async () => {
        spinVisible.value = true
        try {
          const isDeleteStaged = newStaged?.sectionId?.startsWith('staged-delete-')
          let nextPublishedDocsDataByDocId = {}
          if (isDeleteStaged) {
            const { draftSectionByWallet = {} } = props.stagedSection
            const combinedWallets = {}

            for (const walletDraft of Object.values(draftSectionByWallet)) {
              if (isEmpty(walletDraft?.updatedDataBySectionId) && !hasUpdatedDoc(walletDraft)) continue
              const result = await getPublishedDocsDataByDocId(walletDraft)
              Object.assign(combinedWallets, result)
            }

            nextPublishedDocsDataByDocId = combinedWallets
          } else {
            nextPublishedDocsDataByDocId = await getPublishedDocsDataByDocId()
          }

          if (cancelled || runId !== publishedDocDiffRunId) return

          // delete case: 合併各 wallet 的 updatedDoc 傳入 fillDocDiffsAndReleaseContent
          const fillUpdatedDocOverride = isDeleteStaged
            ? Object.values(newStaged.draftSectionByWallet || {}).reduce(
              (acc, { updatedDoc }) => ({ ...acc, ...normalizeUpdatedDocByDocId(updatedDoc) }),
              {},
            )
            : null
          await fillDocDiffsAndReleaseContent(nextPublishedDocsDataByDocId, fillUpdatedDocOverride)

          publishedDocsDataByDocId.value = nextPublishedDocsDataByDocId
          selectedKeys.value = sortDocIds(Object.keys(nextPublishedDocsDataByDocId))
          selectedDocId.value = selectedKeys.value[0] || '' // eslint-disable-line prefer-destructuring
          console.log('[StagedChange] watch draftSection & stagedSection', hasDraftPublishContent(newDraft), isEmpty(nextPublishedDocsDataByDocId))
          if (!isDeleteStaged && hasDraftPublishContent(newDraft) && isEmpty(nextPublishedDocsDataByDocId)) {
            if (!areDraftOriginsReady(newDraft)) {
              console.warn('[StagedChange] skip empty-draft handling: origin sections not ready yet', newDraft)
            } else if (hasUpdatedDoc(newDraft)) {
              console.warn('[StagedChange] draft has updatedDoc but publish preview is empty', newDraft)
              antMessage.error('無法產生子本 platform 發佈預覽，請重新整理後再試')
            } else if (props.type) {
              antMessage.warning('偵測到草稿無異動，準備清空草稿')
              await props.onDiscardDraft()
            }
          }
        } finally {
          if (!cancelled && runId === publishedDocDiffRunId) {
            spinVisible.value = false
          }
        }
      })()
    }, { deep: true, immediate: true })

    watch(() => props.visible, (visible) => {
      if (visible) platformInfoResetKey.value += 1
    })

    watch(
      [
        () => props.highlightKeyword,
        selectedDocId,
        publishedDocsDataByDocId,
        spinVisible,
      ],
      async () => {
        await nextTick()
        ensureHighlightObserver()
        refreshSearchHighlight()
      },
      { deep: true, flush: 'post', immediate: true },
    )

    onMounted(async () => {
      await nextTick()
      ensureHighlightObserver()
      refreshSearchHighlight()
    })

    onBeforeUnmount(() => {
      cancelAnimationFrame(highlightRaf)
      if (highlightObserver) {
        highlightObserver.disconnect()
        highlightObserver = null
      }
    })

    const treeData = computed(() => {
      const docs = publishedDocsDataByDocId.value || {}
      return sortDocIds(Object.keys(docs)).map(docId => docs[docId]).filter(Boolean)
    })

    const collapseActiveKeys = ref([])

    watch(
      () => {
        const diffData = publishedDocsDataByDocId.value?.[selectedDocId.value]?.diffDataBySectionId || {}
        return Object.entries(diffData).map(([sectionId, data]) => (
          `${data?.originSectionData?.docId || selectedDocId.value}-${sectionId}`
        ))
      },
      (keys, prevKeys) => {
        const prevSet = new Set(prevKeys || [])
        const added = keys.filter(key => !prevSet.has(key))
        const stillActive = collapseActiveKeys.value.filter(key => keys.includes(key))
        collapseActiveKeys.value = [...new Set([...stillActive, ...added])]
      },
      { immediate: true },
    )

    expose({ publishedDocsDataByDocId })

    const onChangeReleaseContent = (content) => {
      console.log('[StagedChange] onChangeReleaseContent', content)
      publishedDocsDataByDocId.value[selectedDocId.value].releaseContent = content
    }

    const isMasterDocId = docId => String(docId || '').includes('master')

    const docShouldRelease = (docId) => {
      if (!docId || isMasterDocId(docId)) return false
      return !!publishedDocsDataByDocId.value[docId]?.shouldRelease
    }

    const firstReleasableChildDocId = () => Object.keys(publishedDocsDataByDocId.value || {})
      .find(id => docShouldRelease(id))

    const showReleaseEditorCol = computed(() => {
      const docId = selectedDocId.value
      if (props.type !== 'publish' || !docId || isMasterDocId(docId)) return false
      const entry = publishedDocsDataByDocId.value[docId]
      if (!entry) return false
      if (props.readOnly) return !!entry.shouldRelease
      return !!entry.diffDataBySectionId
    })

    const selectReleasableChildIfNeeded = () => {
      const docId = selectedDocId.value
      if (docId && docShouldRelease(docId)) return
      const firstChild = firstReleasableChildDocId()
      if (!firstChild) return
      selectedDocId.value = firstChild
      selectedKeys.value = [firstChild]
    }

    watch(
      () => props.snapshotPublishedDocsDataByDocId,
      (snapshot) => {
        if (!props.readOnly || isEmpty(snapshot)) return
        selectReleasableChildIfNeeded()
      },
      { immediate: true },
    )

    const getColSpan = (col) => {
      // readOnly（Activity Log）：tree + diff；有更版說明的子本再加上 editor
      if (props.readOnly || hasSnapshot()) {
        if (showReleaseEditorCol.value) {
          if (col === 'tree') return 4
          if (col === 'diff') return 9
          if (col === 'editor') return 11
          return 0
        }
        if (col === 'tree') return 6
        if (col === 'diff') return 18
        return 0
      }

      // type = 'draft', staged = 母本: tree 隱藏 / diff = 24 / editor 隱藏
      // type = 'publish', staged = 母本, selectedDocId = 母本: tree = 4 / diff = 20 / editor 隱藏
      // type = 'publish', staged = 母本, selectedDocId = 子本: tree = 4 / diff = 9 / editor = 11
      // type = 'draft', staged = 子本: tree 隱藏 / diff = 24 / editor 隱藏
      // type = 'publish', staged = 子本: tree 隱藏 / diff = 12 / editor = 12
      // type = 'publish', staged = 子本, updatedSections 只有一個 release-version: tree 隱藏 / diff = 24 / editor = 隱藏
      if (col === 'tree') return 4

      if (col === 'diff') {
        if (props.type === 'draft'
          || !publishedDocsDataByDocId.value[selectedDocId.value]?.diffDataBySectionId
          || (selectedDocId.value || '').includes('master')
        ) return 20
        return 9
      }

      if (col === 'editor') return 11

      return 24
    }

    return {
      isDev,
      changeTypeTitleMap,
      spinVisible,
      platformInfoResetKey,
      highlightRootEl,
      collapseActiveKeys,
      publishedDocsDataByDocId,
      treeData,
      selectedKeys,
      selectedDocId,
      selectedUpdatedDocFieldDiffs,
      onSelect,
      onChangeReleaseContent,
      showReleaseEditorCol,
      getSectionTitle,
      isNoOpSectionDiff,
      formatSortOrderChangeLabel,
      resolveSectionForTitleById,
      getColSpan,
      isEqual,
      titleCase,
      getFieldLabel,
      buildMasterDiffBlocksFromSectionEntry,
      resolvePublishDocLabel,
      resolveParentSectionTitle,
    }
  },
}
</script>

<style>
.published-docs-tree .ant-tree-switcher {
  display: none;
}

.content-viewer {
  min-width: calc(50% - 8px);
  max-height: 500px;
  overflow: auto;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  padding: 16px;
}

code.ant-tag-red, code.ant-tag-green {
  margin-right: 4px;
  white-space: pre-wrap;
  min-height: 20px;
}

.draft-change-row .search-highlight,
.draft-change-highlight-root .search-highlight,
.activity-log .search-highlight {
  background-color: #ffe58f;
  padding: 0;
}
</style>

<style scoped>
.draft-change-spin,
.draft-change-spin :deep(.ant-spin-nested-loading),
.draft-change-spin :deep(.ant-spin-container) {
  height: 100%;
}

.draft-change-highlight-root {
  height: 100%;
}

.draft-change-row {
  height: 100%;
}

.change-col {
  height: 100%;
  border-right: 1px solid #f0f0f0;
}

.change-col h3 {
  padding-left: 12px;
  border-left: 4px solid #1890ff;
}

.change-col:last-of-type {
  border-right: none;
}

.change-tree-col,
.change-diff-col,
.change-editor-col {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

.change-tree-body,
.change-editor-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
}

.change-diff-col {
  position: relative;
}

.change-diff-col .preview-header {
  flex-shrink: 0;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.change-diff-col .preview-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
}

.updated-info {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.45);
}

.applied-docs {
  margin-bottom: 16px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid #eee;
}

.confirmed-switch {
  margin-left: 8px;
}

.updated-doc-diff {
  border-bottom: 1px solid #eee;
  padding-bottom: 16px;
  margin-bottom: 16px;
}

.updated-doc-diff-title {
  border-color: #fa8c16!important;
}

.updated-doc-diff-row {
  margin-bottom: 8px;
}

.platform-info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.platform-info-label {
  flex-shrink: 0;
}
</style>
